import { useSyncExternalStore } from 'react';

import type { ObjectSidebarPanel } from './useObjectSidebar.tsx';

/**
 * A pending request to move keyboard focus back to the element that opened a
 * panel, made when that panel is closed by the user (e.g. its close button).
 *
 * The panel chrome cannot reach the trigger button directly — the trigger lives
 * in a different subtree (the object sidebar control bar) and may even be
 * unmounted or lazy-loaded while the panel is open — so the close path publishes
 * a request here and the trigger consumes it via `useReturnFocusOnPanelClose`.
 *
 * This is the "return focus to the invoking element on close" step of the
 * WAI-ARIA dialog pattern: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 */
export type FocusReturnRequest = {
	panelId: ObjectSidebarPanel['id'];
	/** `Date.now()` at the time of the request, so stale requests can be ignored. */
	requestedAt: number;
};

let snapshot: FocusReturnRequest | null = null;
const listeners = new Set<() => void>();

const emit = (): void => {
	listeners.forEach((listener) => listener());
};

/**
 * Ask the trigger for `panelId` to take focus. Call this when a panel has been
 * closed by a user action (close button, keyboard shortcut) — not when it is
 * closed incidentally, e.g. by navigation.
 */
export const requestFocusReturn = (panelId: ObjectSidebarPanel['id']): void => {
	snapshot = { panelId, requestedAt: Date.now() };
	emit();
};

export const clearFocusReturn = (): void => {
	if (snapshot === null) {
		return;
	}
	snapshot = null;
	emit();
};

const subscribe = (onStoreChange: () => void): (() => void) => {
	listeners.add(onStoreChange);
	return () => {
		listeners.delete(onStoreChange);
	};
};

const getSnapshot = (): FocusReturnRequest | null => snapshot;
const getServerSnapshot = (): FocusReturnRequest | null => null;

export const useFocusReturnRequest = (): FocusReturnRequest | null =>
	useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

/** Resets store state and subscribers. Module state persists between tests otherwise. */
export const clearFocusReturnForTesting = (): void => {
	snapshot = null;
	listeners.clear();
};
